// QuantitySelector.js
import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import css from './QuantitySelector.css';

const QuantitySelector = ({
  initialValue = 1,
  onQuantityChange = () => {},
  onPendingStateChange = () => {}, 
  bookingDates = {},
  timeSlots = [],
  disabled = false,
}) => {
  const [quantity, setQuantity] = useState(initialValue);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setQuantity(initialValue);
  }, [initialValue]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const updateQuantity = nextQuantity => {
    setQuantity(nextQuantity);
    onPendingStateChange(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onQuantityChange(nextQuantity);
      onPendingStateChange(false);
    }, 300);
  };

  const maxAvailableSlots = Math.min(...timeSlots.map(slot => slot.attributes.seats));
  const isDisabled = disabled || !(bookingDates && bookingDates.startDate && bookingDates.endDate);

  const handleDecrease = () => {
    if (!isDisabled && quantity > 1) {
      updateQuantity(quantity - 1);
    }
  };

  const handleIncrease = () => {
    if (!isDisabled && quantity < maxAvailableSlots) {
      updateQuantity(quantity + 1);
    }
  };

  const handleInputChange = e => {
    if (isDisabled) return;
    const newQuantity = parseInt(e.target.value, 10);
    if (!isNaN(newQuantity) && newQuantity >= 1) {
      updateQuantity(newQuantity);
    }
  };

  return (
    <div className={css.container}>
      <div className={css.wrapper}>
        <p className={css.text}>
          <FormattedMessage id="Order.quantity" />
        </p>
        <div className={css.quantityWrapper}>
          <div
            onClick={handleDecrease}
            className={classNames(css.quantityButton, {
              [css.disabled]: isDisabled || quantity <= 1,
            })}
          >
            -
          </div>
          <div className={classNames(css.quantityValue, { [css.disabled]: isDisabled })}>
            {quantity}
          </div>
          <input
            type="number"
            value={quantity}
            onChange={handleInputChange}
            disabled={isDisabled}
            className={css.quantityInput}
          />
          <div
            onClick={handleIncrease}
            className={classNames(css.quantityButton, {
              [css.disabled]: isDisabled || quantity >= maxAvailableSlots,
            })}
          >
            +
          </div>
        </div>
      </div>
      {maxAvailableSlots < initialValue && (
        <p className={css.error}>
          <FormattedMessage id="Order.avaliableQuantity" values={{ maxAvailableSlots }} />
        </p>
      )}
    </div>
  );
};

export default QuantitySelector;
